'use client'
import { useComponentTranslation } from '@core/i18n'
import { Flex, Tag, Text } from '@core/ui-elements'
import {
  _IAssortmentType,
  _IDeliveryStatus,
  _IDeliveryType,
  IDeliveryNormalized,
} from '@data/types'
import { getStatusTime, getTimerLimits } from '@utils/datetime'
import { lastNamesAbbreviator } from '@utils/formatters'
import { CardColors } from '@utils/styles'
import { FC, useState } from 'react'

import { Card } from '../card'
import { CountdownTimer } from '../countdown-timer'
import { StatCard } from '../stats-cards'
import { SuperDakiTag } from '../super-daki-tag'

export interface IPendingOrderCard {
  delivery: IDeliveryNormalized
  minuteCountdown: number
  warningThresholdPercent?: number
  criticalThresholdPercent?: number
  handleClick?: () => void
  hideCustomerOrderCount: boolean
  showTotalAmount?: boolean
}

export const PendingOrderCard: FC<IPendingOrderCard> = ({
  delivery,
  minuteCountdown,
  warningThresholdPercent,
  criticalThresholdPercent,
  handleClick,
  hideCustomerOrderCount,
  showTotalAmount = true,
}) => {
  const t = useComponentTranslation('PendingOrderCard')

  const [cardColor, setCardColor] = useState<CardColors>(CardColors.gray)

  const isSuperDaki = delivery.assortmentType === _IAssortmentType.plus
  const orderIdSlices = delivery.orderId.split('-')
  const orderId = orderIdSlices[orderIdSlices.length - 1]
  const { warningThresholdMS, criticalThresholdMS, handleColorChange } =
    getTimerLimits({
      minuteCountdown,
      warningThresholdPercent,
      criticalThresholdPercent,
    })

  const isScheduled = delivery?.deliveryType === _IDeliveryType.scheduled

  const currentStatusTime = isScheduled
    ? getStatusTime(delivery.statusHistory, _IDeliveryStatus.processed)
    : getStatusTime(delivery.statusHistory, _IDeliveryStatus.pre_processed)

  const handleTimeChange = (time: number) => {
    if (!warningThresholdMS || !criticalThresholdMS) return

    const color = handleColorChange(time)

    if (cardColor !== color) {
      setCardColor(color)
    }
  }

  return (
    <Card
      color={isSuperDaki ? CardColors.blue : cardColor}
      secondaryArea={
        <Flex
          flexDirection={'row'}
          gap={'0.5rem'}
          justifyContent={'space-between'}
          width={'100%'}
        >
          <Flex flexDirection={'row'} gap={'0.25rem'}>
            <Text>{orderId}</Text>
            {!hideCustomerOrderCount && delivery.customer.orderCount ? (
              <Tag backgroundColor="white">
                {t('Order', { count: delivery.customer.orderCount })}
              </Tag>
            ) : null}
          </Flex>

          {showTotalAmount && (
            <Tag backgroundColor="white">
              {t('Item', { count: delivery.totalAmount || 0 })}
            </Tag>
          )}
        </Flex>
      }
      onClick={handleClick}
    >
      <StatCard
        label={lastNamesAbbreviator(delivery.customer.name)}
        rightSize={
          <Flex flexWrap={'nowrap'} gap={'0.5rem'}>
            <SuperDakiTag showTag={isSuperDaki} />
            <CountdownTimer
              bgColor="white"
              callAtTime={handleTimeChange}
              colorIcon={cardColor}
              countTime={(minuteCountdown || 0) * 60 + 1}
              targetTime={currentStatusTime}
            />
          </Flex>
        }
      />
    </Card>
  )
}
